import { Dispatch, ReactNode, SetStateAction, useState } from 'react'
import {
  Avatar,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  useTheme,
} from '@mui/material'

import { ErrorLogFormModal } from '../error-log-form'
import { useMe } from 'src/hooks'

import { IErrorLogBaseWithId, IErrorLogDto } from 'src/types'
import { getTableTimestamp } from 'src/common/utils'
import { useStyles } from './styles'
import { HelperWrapper } from 'src/components/helper-wrapper'
import { GetIcon } from 'src/assets/icons/v2/GetIcon'
import { PatchIcon } from 'src/assets/icons/v2/PatchIcon'
import { PutIcon } from 'src/assets/icons/v2/PutIcon'
import { PostIcon } from 'src/assets/icons/v2/PostIcon'
import { DeleteIcon } from 'src/assets/icons/v2/DeleteIcon'
import { EyeOpenedIcon } from 'src/assets/icons/v2/EyeOpenedIcons'
import { EmptyUserIcon } from 'src/assets/icons/v2/EmptyUserIcon'

interface IErrorLogsTableProps {
  errorLogs?: IErrorLogDto[] | void
  sort: string
  setSort: Dispatch<SetStateAction<string>>
}

interface IMethodObj {
  GET: ReactNode
  POST: ReactNode
  PUT: ReactNode
  PATCH: ReactNode
  DELETE: ReactNode
}

const ErrorLogsTable = ({ errorLogs, sort, setSort }: IErrorLogsTableProps) => {
  const [formInitValues, setFormInitValues] = useState<IErrorLogBaseWithId | null>(null)
  const { data: user } = useMe()
  const classes = useStyles()

  function extractErrorMessage(text: string) {
    const specialSymbols = ['(', '{', '[', '=', ':', ';']
    const splitIndex = Math.min(
      ...specialSymbols.map((symbol) => text.indexOf(symbol)).filter((index) => index !== -1),
    )
    const errorMessage = text.substring(0, splitIndex).trim()

    return errorMessage
  }

  const theme = useTheme()

  const methodObj: IMethodObj = {
    GET: <GetIcon size={36} />,
    POST: <PostIcon size={36} />,
    PUT: <PutIcon size={36} />,
    PATCH: <PatchIcon size={36} />,
    DELETE: <DeleteIcon size={36} />,
  }

  const handleSortChange = (sortName: string) => () => {
    setSort((prevSort) =>
      prevSort.split('-')[1] === 'asc' ? `${sortName}-desc` : `${sortName}-asc`,
    )
  }

  const handleOpen = (val: IErrorLogBaseWithId) => () => {
    setFormInitValues(val)
  }

  const handleClose = () => {
    setFormInitValues(null)
  }

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow 
              style={{ border: 0 }} 
              sx={{ 'td, th': { border: 0 } }}
            >
              <TableCell 
                align="left" 
                width="20%"
              >
                <TableSortLabel
                  onClick={handleSortChange('user_email')}
                  active={sort.split('-')[0] === 'user_email'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="table-user-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell 
                align="left" 
                width="20%"
              >
                <TableSortLabel
                  onClick={handleSortChange('account_name')}
                  active={sort.split('-')[0] === 'account_name'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="table-account-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell 
                align="center" 
                width="7%"
              >
                <TableSortLabel
                  onClick={handleSortChange('method')}
                  active={sort.split('-')[0] === 'method'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="method-filter-sort" />
                </TableSortLabel>
              </TableCell>
              <TableCell 
                align="left" 
                width="23%"
              >
                <TableSortLabel
                  onClick={handleSortChange('message')}
                  active={sort.split('-')[0] === 'message'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="error-logs-message-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell 
                align="center" 
                width="20%"
              >
                <TableSortLabel
                  onClick={handleSortChange('created_at')}
                  active={sort.split('-')[0] === 'created_at'}
                  direction={sort.split('-')[1] as 'desc' | 'asc'}
                  sx={{
                    marginLeft: "20px"
                  }}
                >
                  <HelperWrapper t="email-logs-date-column" />
                </TableSortLabel>
              </TableCell>
              <TableCell 
                align="center" 
                width="10%" 
              />
            </TableRow>
          </TableHead>
          <TableBody>
            {errorLogs?.map((row) => {
              return (
                <TableRow
                  key={row.id}
                  style={{
                    borderTop: `1px solid ${theme.palette.custom.grey}`,
                    borderBottom: `1px solid ${theme.palette.custom.grey}`,
                  }}
                  sx={{ 'td, th': { border: 0 } }}
                >
                  <TableCell width="20%">
                    <div className={classes.iconWrapper}>
                      {row.user && (
                        <Avatar
                          src={row.user.image_small || row.user.image}
                          alt={row.user.email}
                          variant="circular"
                        />
                      )}
                      {row.user?.name ?? row.user?.email}
                    </div>
                    {!row.user_email && <EmptyUserIcon size={36} />}
                  </TableCell>
                  <TableCell width="20%">
                    <div className={classes.iconWrapper}>
                      {row.account && (
                        <Avatar
                          src={row.account.image_small || row.account.image}
                          alt={row.account.account_holder}
                          variant="circular"
                        />
                      )}
                      {row.account?.account_holder ?? row.account_name}
                    </div>
                    {!row.account_name && <EmptyUserIcon size={36} />}
                  </TableCell>
                  <TableCell className={classes.messageCell} width="7%" align="center">
                    {methodObj[row.method as keyof IMethodObj]}
                  </TableCell>
                  <TableCell className={classes.messageCell} width="23%">
                    {extractErrorMessage(row.message).charAt(0).toUpperCase() +
                      extractErrorMessage(row.message).slice(1)}
                  </TableCell>
                  <TableCell 
                    align="center" 
                    width="20%"
                  >
                    {getTableTimestamp({ date: row.created_at, timezone: user?.timezone })}
                  </TableCell>
                  <TableCell 
                    align="center" 
                    width="10%"
                  >
                    <div 
                      onClick={handleOpen(row)} 
                      className={classes.button}
                    >
                      <EyeOpenedIcon size={24} />
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <ErrorLogFormModal
        handleClose={handleClose}
        open={!!formInitValues}
        formValues={formInitValues}
      />
    </>
  )
}

export { ErrorLogsTable }
